<template>
  <CommonPopup
    @close="handleClose"
  >
    <div class="dynamic-popup">
      <img
        v-if="currentStepMeta.image"
        :src="currentStepMeta.image"
        alt="Dynamic Popup Image"
        class="dynamic-popup__image"
      >
      <div
        :class="titleClasses"
      >
        <TypographyComp
          v-editor-inline="currentStepMeta.title"
          preset="heading-s"
          :custom-styles="titleCustomStyles"
        />
      </div>
      <div
        class="dynamic-popup__description"
      >
        <TypographyComp
          v-editor-inline="currentStepMeta.description"
          preset="text-s"
        />
      </div>
      <div
        v-if="currentStepMeta.buttons?.length"
        class="dynamic-popup__buttons"
      >
        <CommonButton
          v-for="button in currentStepMeta.buttons"
          :key="button.text"
          :theme="button.theme || 'primary'"
          :to="button.link || null"
          @click="handleClick(button)"
        >
          <span>
            <span v-editor-inline="button.text" />
          </span>
        </CommonButton>
      </div>
      <div
        v-if="isProgressShown"
        class="dynamic-popup__progress"
      >
        <div
          v-for="step in maxSteps"
          :key="step"
          :class="progressDotClass(step)"
        />
      </div>
    </div>
  </CommonPopup>
</template>

<script setup lang='ts'>
import CommonPopup from '@common/components/popups/CommonPopup.vue';
import { usePopup } from '@common/composables/usePopup';
import { PopupType } from '@common/types/popups';
import type { DynamicPopupMeta, DynamicPopupStep, DynamicPopupButton } from '@common/types/popups';

const currentStep = ref(1);
const maxSteps = computed(() => getMeta(PopupType.DYNAMIC)?.steps.length || 1);

const { getMeta } = usePopup();

const meta = computed<DynamicPopupMeta>(() => getMeta(PopupType.DYNAMIC) as DynamicPopupMeta);
const currentStepMeta = computed<DynamicPopupStep>(() => meta.value.steps[currentStep.value - 1] as DynamicPopupStep);

const { closePopup } = usePopup();

const handleClick = (button: DynamicPopupButton) => {
  if (button.callback) {
    button.callback();
    closePopup();
    return;
  }
  if (button.link || !button.isContinueButton) return;
  if (currentStep.value < maxSteps.value) {
    currentStep.value++;
  }
  else {
    closePopup();
  }
};

const handleClose = () => {
  // closePopup();
};

const titleClasses = computed(() => [
  'dynamic-popup__title',
  {
    'dynamic-popup__title--with-image': currentStepMeta.value.image,
  },
]);

const isProgressShown = computed(() => maxSteps.value > 1);

const progressDotClass = (step: number) => [
  'dynamic-popup__progress-dot',
  {
    'dynamic-popup__progress-dot--active': currentStep.value >= step,
  },
];

const titleCustomStyles = computed(() => ({
  'text-transform': 'none',
}));
</script>

<style lang='scss'>
.dynamic-popup {
  display: flex;
  flex-direction: column;
  text-align: center;

  &__image {
    position: absolute;
    top: 50%;
    left: -11rem;
    width: 42.038rem;
    height: 39.5335rem;
    transform: translateX(-50%) translateY(-50%);
    object-fit: cover;
    z-index: $z-backstage;

    @include layout-aspect-mobile {
      top: -7.3rem;
      left: 50%;
      transform: translateX(-50%) translateY(-50%);
      width: 30.0616rem;
      height: 28.475rem;
    }
  }

  &__title {
    // font-size: 4.8rem;
    // font-weight: 700;
    // line-height: 4.56rem;
    // letter-spacing: -0.096rem;

    &--with-image {
      // padding-top: 2.8rem;
    }
  }

  &__description {
    margin-top: 1.2rem;
    font-size: 1.6rem;
    font-weight: 700;
    line-height: 2rem;
    letter-spacing: -0.064rem;
  }

  &__buttons {
    margin-top: 3.2rem;
    display: flex;
    flex-direction: column;
    gap: 1.6rem;
    justify-content: center;
    align-items: center;
  }

  &__progress {
    margin-top: 2.4rem;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.8rem;
  }

  &__progress-dot {
    width: 0.8rem;
    height: 0.8rem;
    border-radius: 50%;
    background-color: #e0e0e0;
    transition: background-color 0.3s ease;

    &--active {
      background-color: #000000;
    }
  }
}
</style>
